import React, { useState, useCallback, useMemo } from "react";
import { useConfig, useDiscounts, useTicket } from "../../store/Store";
import { applyGiftCard, applyPromoCode } from "../payment/utils/paymentHelpers";
import { useTranslation } from "react-i18next";

export const GiftCardInput = () => {
  const { t } = useTranslation();

  const [giftCardCode, setGiftCardCode] = useState("");
  const { data: ticketData } = useTicket();
  const { accessToken } = useConfig();
  const { applyingGiftCard } = useDiscounts();

  const disabled = useMemo(
    () =>
      !giftCardCode.trim() ||
      applyingGiftCard ||
      ticketData.resource.status === 1,
    [giftCardCode, applyingGiftCard, ticketData]
  );

  const handleApply = useCallback(() => {
    if (giftCardCode.trim()) {
      applyGiftCard(
        giftCardCode,
        ticketData?.resource?.company?.id,
        accessToken
      );
      setGiftCardCode("");
    }
  }, [giftCardCode, ticketData, accessToken]);

  return (
    <div
      className="flex items-center mb-3 max-w-[300px]"
      data-testid="gift-card-input"
    >
      <input
        type="text"
        placeholder={t("discounts.gift_card")}
        value={giftCardCode}
        onChange={(e) => setGiftCardCode(e.target.value)}
        className="flex-1 mr-2 text-sm p-1.5 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <button
        onClick={handleApply}
        disabled={disabled}
        className={`min-w-[80px] px-3 py-1.5 text-sm rounded-md text-white flex items-center justify-center gap-1 ${
          !disabled
            ? "bg-primary hover:bg-primary-hover"
            : "bg-primary-disabled cursor-not-allowed"
        }`}
      >
        <span>{t("discounts.apply")}</span>
        {applyingGiftCard && (
          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

export const PromoCodeInput = () => {
  const { t } = useTranslation();

  const [promoCode, setPromoCode] = useState("");
  const { data: ticketData } = useTicket();
  const { accessToken } = useConfig();
  const { applyingPromoCode } = useDiscounts();

  const disabled = useMemo(
    () =>
      !promoCode.trim() ||
      applyingPromoCode ||
      ticketData.resource.status === 1,
    [promoCode, applyingPromoCode, ticketData]
  );

  const handleApply = useCallback(() => {
    if (promoCode.trim()) {
      applyPromoCode(promoCode, ticketData.resource.company.id, accessToken);
      setPromoCode("");
    }
  }, [promoCode, ticketData, accessToken]);

  return (
    <div
      className="flex items-center max-w-[300px]"
      data-testid="promo-code-input"
    >
      <input
        type="text"
        placeholder={t("discounts.promo_code")}
        value={promoCode}
        onChange={(e) => setPromoCode(e.target.value)}
        className="flex-1 mr-2 text-sm p-1.5 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary"
      />
      <button
        onClick={handleApply}
        disabled={disabled}
        className={`min-w-[80px] px-3 py-1.5 text-sm rounded-md text-white flex items-center justify-center gap-1 ${
          !disabled
            ? "bg-primary hover:bg-primary-hover"
            : "bg-primary-disabled cursor-not-allowed"
        }`}
      >
        {t("discounts.apply")}
        {applyingPromoCode && (
          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
      </button>
    </div>
  );
};
